import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SubcontractTable from '../../components/SubcontractTable';
import SubcontractForm from '../../components/SubcontractForm';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import ModalComponent from '../../components/ErrorsModal';
import Loader from '../../components/Loader';
import { Subcontractor, SubcontractorRequest } from '../../types/subcontractTypes';
import {
    getSubcontractors,
    createSubcontractor,
    updateSubcontractor,
    deleteSubcontractor
} from '../../api/subcontractApi';
import { getActivities } from '../../api/activityApi';
import { getActiveProjects } from '../../api/projectApi';
import { Project } from '../../types/projectTypes';
import { getSubcontractPermissions, getCurrentRole } from '../../contexts/constants/subcontractPermissions';

/** Sección reservada para filas de configuración del sistema; no es una especialidad real. */
const CONFIG_SECTION = 'CONFIGURACION';

const Subcontracts: React.FC = () => {
    const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
    const [sections, setSections] = useState<string[]>([]);
    const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editing, setEditing] = useState<Subcontractor | null>(null);

    const [deleteTarget, setDeleteTarget] = useState<Subcontractor | null>(null);

    const [resultModalOpen, setResultModalOpen] = useState(false);
    const [resultMessage, setResultMessage] = useState('');
    const [resultTitle, setResultTitle] = useState<string | undefined>(undefined);
    const [resultSeverity, setResultSeverity] = useState<'error' | 'success' | 'warning'>('error');

    const permissions = useMemo(() => getSubcontractPermissions(getCurrentRole()), []);

    // La carga inicial corre una sola vez al montar; fetchAll se redefine en cada render
    // y no debe reejecutar el efecto.
    useEffect(() => {
        fetchAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showError = (message: string) => {
        setResultTitle(undefined);
        setResultSeverity('error');
        setResultMessage(message);
        setResultModalOpen(true);
    };

    const showSuccess = (message: string) => {
        setResultTitle('Operación realizada');
        setResultSeverity('success');
        setResultMessage(message);
        setResultModalOpen(true);
    };

    const fetchAll = async () => {
        try {
            setLoading(true);

            const [subcontractorsData, activitiesData, projectsData] = await Promise.all([
                getSubcontractors(),
                getActivities(),
                getActiveProjects()
            ]);

            setSubcontractors(subcontractorsData);

            // Las especialidades son las secciones ya cargadas en el mantenedor de Actividades.
            // Se deduplica sin distinguir mayúsculas para no mostrar dos botones que se ven
            // iguales, como pasa hoy con "Instalación de Faena" e "Instalación de faena".
            const seen = new Map<string, string>();
            activitiesData.forEach((activity: any) => {
                const section = (activity.section || '').trim();
                if (!section || section === CONFIG_SECTION) {
                    return;
                }
                const key = section.toLowerCase();
                if (!seen.has(key)) {
                    seen.set(key, section);
                }
            });
            setSections(Array.from(seen.values()).sort((a, b) => a.localeCompare(b)));

            const activeProjects = (Array.isArray(projectsData) ? projectsData : [])
                .filter((project: Project) => project.status === true || project.status === undefined)
                .map((project: Project) => ({ id: project.id as number, name: project.name as string }));
            setProjects(activeProjects);
        } catch (error: any) {
            console.error('Error al cargar el mantenedor de subcontratos', error);
            showError('No se pudo cargar la información de subcontratos.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditing(null);
        setIsFormOpen(true);
    };

    const handleOpenEdit = (subcontractor: Subcontractor) => {
        setEditing(subcontractor);
        setIsFormOpen(true);
    };

    const handleSubmit = async (request: SubcontractorRequest) => {
        try {
            setSaving(true);
            if (editing) {
                await updateSubcontractor(editing.id, request);
            } else {
                await createSubcontractor(request);
            }
            setIsFormOpen(false);
            setEditing(null);
            await fetchAll();
        } catch (error: any) {
            // El backend devuelve los mensajes literales definidos en las reglas de negocio,
            // por ejemplo el de RUT ya registrado.
            const backendMessage = error.response?.data?.mensaje
                || error.response?.data?.message
                || 'No se pudo guardar la empresa subcontratista.';
            showError(backendMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return;
        const target = deleteTarget;
        setDeleteTarget(null);
        try {
            await deleteSubcontractor(target.id);
            setSubcontractors(prev => prev.filter(item => item.id !== target.id));
            showSuccess(`Se eliminó la empresa ${target.businessName}.`);
        } catch (error: any) {
            const backendMessage = error.response?.data?.mensaje
                || error.response?.data?.message
                || 'No se pudo eliminar la empresa subcontratista.';
            showError(backendMessage);
        }
    };

    return (
        <Box sx={{ padding: 3 }}>
            {loading && <Loader />}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#004F86' }}>
                    Gestión de Subcontratos
                </Typography>
                {permissions.canCreate && (
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreate}
                    >
                        Agregar Empresa Subcontratista
                    </Button>
                )}
            </Box>

            <SubcontractTable
                subcontractors={subcontractors}
                permissions={permissions}
                onEdit={handleOpenEdit}
                onDelete={setDeleteTarget}
            />

            <SubcontractForm
                open={isFormOpen}
                onClose={() => { setIsFormOpen(false); setEditing(null); }}
                onSubmit={handleSubmit}
                subcontractor={editing}
                availableSections={sections}
                availableProjects={projects}
                permissions={permissions}
                saving={saving}
            />

            <ConfirmDeleteModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onSubmit={handleConfirmDelete}
                id={deleteTarget?.id ?? 0}
                name={deleteTarget?.businessName ?? ''}
                title={'SUBCONTRACTOR'}
            />

            <ModalComponent
                open={resultModalOpen}
                onClose={() => setResultModalOpen(false)}
                message={resultMessage}
                title={resultTitle}
                severity={resultSeverity}
            />
        </Box>
    );
};

export default Subcontracts;
